/**
 * paymentMethodAggregation.ts — Agregación de cobros por slot inmutable de pago.
 *
 * Regla anti-fantasma: sumar siempre por `method` (slot id), nunca por el texto
 * del snapshot. Los nombres editables solo sirven para mostrar en UI.
 */
import type { Venta } from "../types/caja.types";
import type { PaymentMethodsConfig } from "@/lib/paymentMethodsConfig";
import {
  getDefaultPaymentMethodLabel,
  getPaymentMethodById,
} from "@/lib/paymentMethodsConfig";

/** IDs legacy → slots canónicos (solo para agregación matemática). */
const LEGACY_SLOT_MAP: Record<string, string> = {
  nequi: "digital_1",
  daviplata: "digital_2",
  transferencia: "digital_3",
};

/** Orden visual preferido en resúmenes de cierre. */
const SLOT_ORDER: string[] = [
  "efectivo",
  "tarjeta",
  "digital_1",
  "digital_2",
  "digital_3",
  "digital_4",
  "digital_5",
  "digital_6",
];

export function normalizePaymentSlotId(id: string): string {
  const trimmed = (id || "").trim();
  if (!trimmed) return trimmed;
  // Case-insensitive: una venta guardada como "Daviplata"/" DAVIPLATA " debe
  // mapear al mismo slot que "daviplata". Sin esto, el método caía en "otros"
  // y el resumen (Efectivo/Nequi/Daviplata/Tarjeta) no cuadraba con las ventas.
  const lower = trimmed.toLowerCase();
  if (LEGACY_SLOT_MAP[lower]) return LEGACY_SLOT_MAP[lower];
  if (lower === "efectivo" || lower === "tarjeta") return lower;
  return trimmed;
}

export interface PaymentLineFromVenta {
  slotId: string;
  amount: number;
  labelSnapshot?: string;
  ventaId: string;
  fecha: string;
  userName?: string;
  breakdownIndex?: number;
}

export interface SlotAggregationItem {
  id: string;
  ventaId: string;
  fecha: string;
  monto: number;
  userName?: string;
  labelSnapshot?: string;
}

export interface SlotAggregation {
  slotId: string;
  total: number;
  count: number;
  labelsSeen: Set<string>;
  items: SlotAggregationItem[];
}

export interface ResumenSlotRow {
  slotId: string;
  label: string;
  total: number;
  count: number;
  renamedDuringShift?: boolean;
  labelVariants?: string[];
  kind: "efectivo" | "tarjeta" | "digital" | "other";
}

export interface SlotDisplayInfo {
  label: string;
  renamedDuringShift: boolean;
  labelVariants: string[];
  kind: "efectivo" | "tarjeta" | "digital" | "other";
}

export function getPaymentMethodKind(
  slotId: string,
  cfg?: PaymentMethodsConfig | null,
): ResumenSlotRow["kind"] {
  const slot = normalizePaymentSlotId(slotId);
  if (slot === "efectivo") return "efectivo";
  if (slot === "tarjeta") return "tarjeta";
  const fromCfg = cfg ? getPaymentMethodById(cfg, slot)?.kind : undefined;
  if (fromCfg === "digital" || fromCfg === "tarjeta") return fromCfg;
  if (slot.startsWith("digital_")) return "digital";
  if (["nequi", "daviplata", "transferencia"].includes(slot)) return "digital";
  return "other";
}

/** Métodos que entran al cuadre de transferencias (excluye efectivo). */
export function isCuadreSlot(
  slotId: string,
  cfg?: PaymentMethodsConfig | null,
): boolean {
  const kind = getPaymentMethodKind(slotId, cfg);
  return kind === "tarjeta" || kind === "digital";
}

export function extractPaymentLinesFromVenta(
  venta: Venta,
): PaymentLineFromVenta[] {
  if (venta.estado === "anulada" || venta.estado === "devolucion-total") {
    return [];
  }

  const base = {
    ventaId: venta.id,
    fecha: venta.fecha,
    userName: venta.userName,
  };

  if (venta.metodoPago === "mixto" && venta.detalleMetodoPago?.breakdown) {
    return venta.detalleMetodoPago.breakdown
      .filter((b) => (b.amount || 0) > 0)
      .map((b, idx) => ({
        ...base,
        slotId: normalizePaymentSlotId(b.method),
        amount: b.amount,
        labelSnapshot: b.methodLabel,
        breakdownIndex: idx,
      }));
  }

  if (
    !venta.metodoPago ||
    venta.metodoPago === "mixto" ||
    venta.metodoPago === "credito"
  ) {
    return [];
  }

  return [
    {
      ...base,
      slotId: normalizePaymentSlotId(venta.metodoPago),
      amount: venta.total || 0,
      labelSnapshot: venta.metodoPagoLabel,
    },
  ];
}

function pushLabel(labelsSeen: Set<string>, label?: string | null): void {
  const snap = (label || "").trim();
  if (snap) labelsSeen.add(snap);
}

export function aggregateVentasByPaymentSlot(
  ventas: Venta[],
): Map<string, SlotAggregation> {
  const map = new Map<string, SlotAggregation>();

  ventas.forEach((venta) => {
    extractPaymentLinesFromVenta(venta).forEach((line) => {
      const existing = map.get(line.slotId) ?? {
        slotId: line.slotId,
        total: 0,
        count: 0,
        labelsSeen: new Set<string>(),
        items: [],
      };

      pushLabel(existing.labelsSeen, line.labelSnapshot);

      const itemId =
        line.breakdownIndex != null
          ? `${line.ventaId}#${line.breakdownIndex}`
          : line.ventaId;

      existing.items.push({
        id: itemId,
        ventaId: line.ventaId,
        fecha: line.fecha,
        monto: line.amount,
        userName: line.userName,
        labelSnapshot: line.labelSnapshot,
      });
      existing.count += 1;
      existing.total += line.amount;
      map.set(line.slotId, existing);
    });
  });

  return map;
}

export function getSlotDisplayLabel(
  slotId: string,
  labelsSeen: Set<string>,
  cfg?: PaymentMethodsConfig | null,
): SlotDisplayInfo {
  const kind = getPaymentMethodKind(slotId, cfg);
  const labelVariants = [...labelsSeen].filter(Boolean).sort();

  const configLabel = cfg
    ? getPaymentMethodById(cfg, slotId)?.label
    : undefined;
  const currentLabel =
    configLabel?.trim() || getDefaultPaymentMethodLabel(slotId) || slotId;

  let label = currentLabel;
  if (labelVariants.length === 1 && !configLabel) {
    label = labelVariants[0];
  } else if (labelVariants.length === 1 && configLabel) {
    label = configLabel;
  }

  const renamedDuringShift =
    labelVariants.length > 1 ||
    (labelVariants.length === 1 &&
      labelVariants[0].toLowerCase() !== currentLabel.toLowerCase());

  return {
    label,
    renamedDuringShift,
    labelVariants,
    kind,
  };
}

export function sortSlotIds(a: string, b: string): number {
  const ia = SLOT_ORDER.indexOf(a);
  const ib = SLOT_ORDER.indexOf(b);
  if (ia >= 0 && ib >= 0) return ia - ib;
  if (ia >= 0) return -1;
  if (ib >= 0) return 1;
  return a.localeCompare(b, "es");
}

export function buildResumenSlotRows(
  ventas: Venta[],
  cfg?: PaymentMethodsConfig | null,
): ResumenSlotRow[] {
  const aggregated = aggregateVentasByPaymentSlot(ventas);

  return [...aggregated.entries()]
    .sort(([a], [b]) => sortSlotIds(a, b))
    .map(([slotId, agg]) => {
      const display = getSlotDisplayLabel(slotId, agg.labelsSeen, cfg);
      return {
        slotId,
        label: display.label,
        total: agg.total,
        count: agg.count,
        renamedDuringShift: display.renamedDuringShift,
        labelVariants: display.labelVariants,
        kind: display.kind,
      };
    });
}

/** Mapea slot → bucket legacy de propina/resumen (4 columnas fijas en ventas viejas). */
export function slotToLegacyBucket(
  slotId: string,
): "efectivo" | "nequi" | "daviplata" | "tarjeta" | "otros" {
  const slot = normalizePaymentSlotId(slotId);
  if (slot === "efectivo") return "efectivo";
  if (slot === "tarjeta") return "tarjeta";
  if (slot === "digital_1") return "nequi";
  if (slot === "digital_2") return "daviplata";
  return "otros";
}

/** Slots que cuentan como ingreso digital en movimientos de caja. */
export function isDigitalIngresoSlot(metodoPago: string): boolean {
  const slot = normalizePaymentSlotId(metodoPago);
  if (slot === "efectivo") return false;
  const kind = getPaymentMethodKind(slot);
  return kind === "digital" || kind === "tarjeta";
}

/** Reparte un monto de venta entre slots según proporción del breakdown. */
export function allocateVentaAmountBySlot(
  venta: Venta,
  amount: number,
): Map<string, number> {
  const lines = extractPaymentLinesFromVenta(venta);
  const result = new Map<string, number>();
  const amt = Math.max(0, Math.round(amount));
  if (lines.length === 0 || amt <= 0) return result;

  if (lines.length === 1) {
    result.set(lines[0].slotId, amt);
    return result;
  }

  const payTotal = lines.reduce((s, l) => s + l.amount, 0) || venta.total;
  if (payTotal <= 0) return result;

  let allocated = 0;
  lines.forEach((line, i) => {
    const isLast = i === lines.length - 1;
    const share = isLast
      ? amt - allocated
      : Math.round((amt * line.amount) / payTotal);
    const add = Math.max(0, share);
    result.set(line.slotId, (result.get(line.slotId) ?? 0) + add);
    allocated += isLast ? add : add;
  });
  return result;
}
